// App.js
import React, { useState } from 'react';
import AppLayout from './components/AppLayout';
import Dashboard from './components/Dashboard';
import EcommerceModule from './components/EcommerceModule';
import UserManagementModule from './components/UserManagementModule';
import MyProfileModule from './components/MyProfileModule';
import LogisticaModule from './components/LogisticaModule';
import GuideManagementModule from './components/GuideManagementModule';
import ReportesModule from './components/ReportesModule';
import NotFoundModule from './components/NotFoundModule';
import InventoryModule from './components/InventoryModule';
import PickingModule from './components/PickingModule';
import PickingModuleV2 from './components/PickingModuleV2';
import AutoAzurInventoryUpdateModule from './components/AutoAzurInventoryUpdateModule';
import TransfersModule from './components/TransfersModule';
import OwnerSpaceModule from './components/OwnerSpaceModule';
import GarantiaModule from './components/GarantiaModule';
import Certificado from './components/certificados/certificado';
import Corte from './sucursales/corte';
import WhatsappChat from './components/WhatsappChat';
import MeliMetricsModule from './components/MeliMetricsModule';

import { useAuth } from './context/AuthContext';
// Se usarán las imágenes importadas localmente
import backgroundImage from './bg.png';
import logoNexusPequeno from './logoblack.png';

/**
 * - Usa REACT_APP_API_URL si está definida (limpiando slashes).
 * - Si no, en dev apunta a http://localhost:3000.
 * - En producción, usa el mismo origin donde está montada la app.
 */
const API_BASE =
  (process.env.REACT_APP_API_URL &&
    process.env.REACT_APP_API_URL.replace(/\/+$/, '')) ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : window.location.origin);

function App() {
  const { user, isLoggedIn, login, logout } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'error' o 'success'
  const [isLoading, setIsLoading] = useState(false);
  const [isPwdVisible, setIsPwdVisible] = useState(false);
  const [isCapsLock, setIsCapsLock] = useState(false);
  
  const [currentView, setCurrentView] = useState('dashboard');

  // Efecto para limpiar estados de login cuando el usuario cierra sesión
  React.useEffect(() => {
    if (!isLoggedIn) {
      setIsLoading(false);
      setPassword('');
    }
  }, [isLoggedIn]);

  const navigateTo = (viewName) => {
    console.log('App.js: Navegando a la vista:', viewName);
    setCurrentView(viewName);
  };

  // Sanitizador SOLO para campos no sensibles (usuario, etc.)
  // (No se usa en este login, pero puede ser usado por otros componentes)
  const sanitizeInput = (input, maxLength = 50) => {
    if (typeof input !== 'string') return '';
    const cleaned = input
      .replace(/[<>"'%;()&+]/g, '')
      .replace(/(--|\b(select|insert|update|delete|drop|union|or|and)\b)/gi, '')
      .replace(/[{}[\]\\]/g, '')
      .trim();
    return cleaned.substring(0, maxLength);
  };

  const handleUsernameChange = (e) => {
    // Simplemente actualiza el estado
    setUsername(e.target.value);
  };

  // ⚠️ No sanitizar contraseña: respeta caracteres especiales.
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  // Nuevo handler para el aviso de Bloq Mayús
  const handlePasswordKeyUp = (e) => {
    try {
      const on = e.getModifierState && e.getModifierState('CapsLock');
      setIsCapsLock(on);
    } catch (error) {
      console.warn("No se pudo detectar CapsLock: ", error);
    }
  };

  // Nuevo handler para mostrar/ocultar contraseña
  const togglePasswordVisibility = () => {
    setIsPwdVisible(prev => !prev);
  };

  // Validación simple
  const validate = () => {
    if (!username.trim() || !password) {
      setMessage('Por favor completa ambos campos.');
      setMessageType('error');
      return false;
    }
    setMessage('');
    setMessageType('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Simulación de error rápido
    if (username.trim().toLowerCase() === 'error') {
      setMessage('Error: Credenciales inválidas.');
      setMessageType('error');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // credentials: 'include', // Descomenta si usas cookies
        body: JSON.stringify({ user: username, password: password }),
      });

      if (response.status === 429) {
        const textMessage = await response.text();
        setMessage(`Error: ${textMessage}`);
        setMessageType('error');
        console.error('App.js: Error de límite de tasa:', textMessage);
        setIsLoading(false); // Asegúrate de detener la carga
        return;
      }

      const ct = response.headers.get('content-type') || '';
      let data;
      if (ct.includes('application/json')) {
        data = await response.json();
      } else {
        const raw = await response.text();
        data = { mensaje: raw?.slice(0, 200) || 'Error desconocido' };
      }

      if (response.ok) {
        // Guarda token/usuario en tu AuthContext como ya haces
        login(data.usuario, data.token);
        setMessage('¡Inicio de sesión correcto! Redirigiendo…');
        setMessageType('success');
        console.log('App.js: Login exitoso, datos recibidos:', data);
        setIsLoading(false);
        setCurrentView('dashboard');
      } else {
        setMessage(`Error: ${data.mensaje || 'Credenciales inválidas.'}`);
        setMessageType('error');
        console.error('App.js: Error en el login:', data);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('App.js: Error de red o servidor:', error);
      console.error('App.js: Error de conexión con el servidor. Intenta de nuevo más tarde.');
      setMessageType('error');
      setIsLoading(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen w-full flex overflow-hidden font-sans select-none bg-white">
        {/* LEFT PANEL: Background Image bg.png */}
        <div 
          className="hidden md:block flex-1 bg-cover bg-center bg-no-repeat relative"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        >
          {/* Subtle gradient vignette blending into white panel */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/10 pointer-events-none" />
        </div>

        {/* RIGHT PANEL: Login Form Panel */}
        <div className="w-full md:w-[460px] lg:w-[500px] shrink-0 bg-white flex flex-col justify-center p-6 sm:p-10 md:p-12 overflow-y-auto relative z-10 border-l border-slate-100 shadow-xl">
          
          <div className="w-full max-w-[360px] mx-auto py-4">
            
            {/* Logo */}
            <div className="flex justify-start mb-8">
              <img
                src={logoNexusPequeno}
                alt="NEXXUS Logo"
                className="h-12 w-auto object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/180x60/transparent/ea580c?text=NEXXUS';
                }}
              />
            </div>

            {/* Status Indicator Badge */}
            <div className="flex items-center gap-2 mb-6">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <span className="text-xs font-bold tracking-wider text-[#475569] uppercase">
                SERVIDOR EN LÍNEA
              </span>
            </div>

            {/* Header Title & Subtitle */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-[34px] font-extrabold text-[#0f172a] leading-tight tracking-tight mb-2">
                Bienvenido<br />de vuelta 👋
              </h1>
              <p className="text-[#64748b] text-sm font-normal">
                Ingresa tus credenciales para acceder al sistema
              </p>
            </div>

            {/* Form */}
            <form id="loginForm" noValidate onSubmit={handleSubmit} className="space-y-5">
              
              {/* Username Input */}
              <div>
                <label htmlFor="username" className="block text-xs font-bold text-[#475569] tracking-wider uppercase mb-2">
                  USUARIO
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-[#8a99ad]">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM5.25 20.125a6.75 6.75 0 0113.5 0 .75.75 0 01-.75.875h-12a.75.75 0 01-.75-.875z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    autoComplete="username"
                    placeholder="admin@llantasregio.com.mx"
                    className="w-full bg-[#eef3fb] text-[#1e293b] text-sm sm:text-base font-medium rounded-xl py-3.5 pl-11 pr-4 border border-transparent focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-100 outline-none transition-all duration-200 placeholder-[#94a3b8]"
                    required
                    value={username}
                    onChange={handleUsernameChange}
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-xs font-bold text-[#475569] tracking-wider uppercase mb-2">
                  CONTRASEÑA
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-[#8a99ad]">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type={isPwdVisible ? 'text' : 'password'}
                    id="password"
                    name="password"
                    placeholder="••••••••••••"
                    className="w-full bg-[#eef3fb] text-[#1e293b] text-sm sm:text-base font-medium rounded-xl py-3.5 pl-11 pr-11 border border-transparent focus:border-orange-400 focus:bg-white focus:ring-2 focus:ring-orange-100 outline-none transition-all duration-200 placeholder-[#94a3b8]"
                    required
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyUp={handlePasswordKeyUp}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    tabIndex="-1"
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-[#8a99ad] hover:text-[#475569] transition-colors"
                  >
                    {isPwdVisible ? (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>

                {isCapsLock && (
                  <p className="text-xs text-orange-600 font-medium mt-1.5 flex items-center gap-1">
                    ⚠️ Bloq Mayús activado
                  </p>
                )}
              </div>

              {/* Login Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full mt-4 py-3.5 px-6 rounded-xl font-bold text-white text-base bg-gradient-to-r from-[#f96417] via-[#f97316] to-[#f59e0b] hover:from-[#ea580c] hover:to-[#d97706] shadow-lg shadow-orange-500/35 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Autenticando...</span>
                  </>
                ) : (
                  <>
                    <span>Acceder al Sistema</span>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </>
                )}
              </button>

              {/* Error / Success Feedback */}
              {message && (
                <div className={`p-3.5 rounded-xl text-sm font-medium flex items-center gap-2.5 animate-fadeIn ${
                  messageType === 'error'
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                }`}>
                  <span>{message}</span>
                </div>
              )}
            </form>

            {/* Separator */}
            <div className="w-full h-px bg-slate-200/80 my-8" />

            {/* Footer */}
            <div className="flex items-center justify-center gap-1.5 text-xs font-medium text-[#94a3b8]">
              <svg className="w-3.5 h-3.5 text-[#94a3b8]" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
              </svg>
              <span>zNexxus © 2026 — Plataforma VORTEX</span>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // --- VISTA PRINCIPAL DE LA APP (CUANDO ESTÁ LOGUEADO) ---
  console.log('App.js: Vista actual a renderizar:', currentView, 'User para la vista:', user);

  return (
    <AppLayout onLogout={logout} navigateTo={navigateTo} currentView={currentView}>
      {(() => {
        switch (currentView) {
          case 'garantia':
            return <GarantiaModule />;
          case 'crear-garantia':
            return <GarantiaModule initialCreateOpen={true} />;
          case 'certificado':
            return <Certificado />;
          case 'dashboard':
            return <Dashboard />;
          case 'owner-space':
            return <OwnerSpaceModule />;
          case 'ecommerce':
            return <EcommerceModule />;
          case 'logistica':
            return <LogisticaModule />;
          case 'guide-management':
            return <GuideManagementModule />;
          case 'transfers':
            return <TransfersModule />;
          case 'reportes':
            return <ReportesModule />;
          case 'users':
            return <UserManagementModule />;
          case 'carriers':
            return <UserManagementModule initialTab="carriers" />;
          case 'inventory':
            return <InventoryModule />;
          case 'picking':
            return <PickingModule />;
          case 'picking-v2':
            return <PickingModuleV2 />;
          case 'my-account':
            return <MyProfileModule />;
          case 'autoazur-inventory-update':
            return <AutoAzurInventoryUpdateModule />;
          case 'meli-metrics':
            return <MeliMetricsModule />;
          case 'whatsapp-chat': // <-- AÑADE ESTA LÍNEA
          return <WhatsappChat />; // <-- AÑADE ESTA LÍNEA
            // seccion sucursales que necesita proteccion con password
          case 'sucursales':
            return (
                <Corte />
            );

          default:
            console.error('App.js: Vista no reconocida:', currentView);
            return <NotFoundModule navigateTo={navigateTo} />;
        }
      })()}
    </AppLayout>
  );
}

export default App;

